import { DeleteOutlined, DownloadOutlined, FilePdfOutlined, SaveOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReportColumn, ReportMeta, ReportParam, ReportResult, ReportView } from "@wms/domain";
import { Alert, App, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Typography } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API_BASE, ApiError, api, errorMessage, qs } from "../api/client";
import { formatDate, formatDateTime } from "../api/format";
import { downloadHeaders } from "../api/download";
import { reportPdfParams } from "../api/renditions";
import { useAuthStore } from "../auth/store";
import { DocumentViewer } from "../components/DocumentViewer";
import { LoadProblem } from "../components/LoadProblem";
import { DateTimeField } from "../components/DateTimeField";
import { RefSelect } from "../components/RefSelect";
import { footerRows } from "./reportTotals";

function ParamInput({ param, ...rest }: { param: ReportParam } & Record<string, unknown>) {
  switch (param.type) {
    case "date":
      return <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} {...rest} />;
    case "datetime":
      // ТЗ 1.2 раздел 10: the slice may be an instant («срез 04.11 в 10:00»), read in the warehouse time zone
      return <DateTimeField {...rest} />;
    case "ref":
      // D04: an empty warehouse means every warehouse this user may see, never the whole platform
      return <RefSelect entity={param.ref!} placeholder={param.ref === "warehouses" ? "Все доступные мне склады" : undefined} {...rest} />;
    case "enum":
      return <Select allowClear={!param.required} options={param.options ?? []} {...rest} />;
    case "number":
      return <InputNumber style={{ width: "100%" }} min={param.min ?? undefined} precision={0} {...rest} />;
    case "boolean":
      return <Switch {...rest} />;
    default:
      return <Input placeholder={param.description ?? undefined} {...rest} />;
  }
}

function cell(column: ReportColumn, value: unknown): React.ReactNode {
  if (value === null || value === undefined) return "";
  switch (column.type) {
    case "datetime":
      return formatDateTime(value);
    case "date":
      return formatDate(value);
    case "boolean":
      return value ? "Да" : "";
    case "number":
      return Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 3 });
    case "percent":
      return `${value}%`;
    default:
      return String(value);
  }
}

function tableColumns(columns: ReportColumn[]) {
  return columns.map((c) => ({
    title: c.label,
    dataIndex: c.name,
    align: c.type === "number" || c.type === "percent" ? ("right" as const) : undefined,
    render: (v: unknown) => cell(c, v),
    sorter: (a: Record<string, unknown>, b: Record<string, unknown>) =>
      c.type === "number" || c.type === "percent" ? Number(a[c.name] ?? 0) - Number(b[c.name] ?? 0) : String(a[c.name] ?? "").localeCompare(String(b[c.name] ?? "")),
  }));
}

/** Serialise form values the way the API expects: dates as YYYY-MM-DD, instants as YYYY-MM-DDTHH:mm. */
function cleanValues(values: Record<string, unknown>, params: ReportParam[]): Record<string, unknown> {
  const types = new Map(params.map((p) => [p.name, p.type]));
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    if (v === undefined || v === null || v === "") continue;
    if (dayjs.isDayjs(v)) clean[k] = types.get(k) === "datetime" ? v.format("YYYY-MM-DDTHH:mm") : v.format("YYYY-MM-DD");
    else clean[k] = v;
  }
  return clean;
}

/** The opposite: a saved view's parameters back into form values. */
function formValues(saved: Record<string, unknown>, params: ReportParam[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of params) {
    const v = saved[p.name];
    if (v === undefined || v === null || v === "") continue;
    out[p.name] = (p.type === "date" || p.type === "datetime") && typeof v === "string" ? dayjs(v) : v;
  }
  return out;
}

/** ТЗ 1.2 раздел 9.4 «Показать подробнее»: the source rows behind one aggregated row, same filters and scope. */
function DrillRows({ code, params, row, columns }: { code: string; params: Record<string, unknown>; row: Record<string, unknown>; columns: ReportColumn[] }) {
  const details = useQuery({
    queryKey: ["reports", code, "details", params, row._key],
    queryFn: () => api.get<{ rows: Record<string, unknown>[]; total: number }>(`/reports/${code}/details`, { params, key: row._key }),
  });
  if (details.isError) return <Alert type="error" showIcon message={errorMessage(details.error)} />;
  return (
    <Table
      rowKey={(_, index) => String(index)}
      size="small"
      loading={details.isFetching}
      dataSource={details.data?.rows ?? []}
      columns={tableColumns(columns)}
      pagination={{ pageSize: 50, hideOnSinglePage: true, showTotal: (t) => `оснований: ${t}` }}
      scroll={{ x: true }}
      data-testid="report-drill"
    />
  );
}

/** «Данные на …» (ТЗ 1.2 раздел 9.4): the slice, the day boundary and whether the rows are complete. */
function ResultMeta({ meta }: { meta: Record<string, unknown> }) {
  const parts: string[] = [];
  if (meta.as_of) parts.push(`Данные на ${formatDateTime(meta.as_of)}`);
  if (meta.watermark) parts.push(`последнее движение ${formatDateTime(meta.watermark)}`);
  if (typeof meta.day_boundary === "string") parts.push(meta.day_boundary);
  if (typeof meta.strategy === "string") parts.push(`порядок слоёв ${meta.strategy}`);
  const complete = meta.complete !== false;
  const note = typeof meta.note === "string" && meta.note ? meta.note : "данные неполные";
  return (
    <Alert
      type={complete ? "info" : "warning"}
      showIcon
      style={{ marginBottom: 12 }}
      data-testid="report-meta"
      message={parts.join(" · ") || "Отчёт сформирован"}
      description={complete ? (typeof meta.definition === "string" ? meta.definition : undefined) : `Неполные данные — это не ноль: ${note}`}
    />
  );
}

export function ReportPage() {
  const { code = "" } = useParams();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [params, setParams] = useState<Record<string, unknown> | null>(null);
  const [viewId, setViewId] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  // KZ-DOC D3: «Просмотр и печать» — the report with the current filters as a stored PDF; XLSX stays as it was
  const [viewing, setViewing] = useState(false);
  // a stable reference from the store (a fresh [] in the selector would re-render forever)
  const boundWarehouses = useAuthStore((s) => s.me?.warehouse_ids);
  const [saveForm] = Form.useForm<{ name: string; is_shared: boolean }>();
  const metaQuery = useQuery({ queryKey: ["reports"], queryFn: () => api.get<ReportMeta[]>("/reports") });
  const meta = metaQuery.data?.find((r) => r.code === code);
  // QA20-003: a report the list does not offer is either closed to the role (403, explained) or does not exist (404)
  const probe = useQuery({
    queryKey: ["reports", "probe", code],
    queryFn: () => api.get<ReportResult>(`/reports/${code}`),
    enabled: !!metaQuery.data && !meta,
    retry: false,
  });
  const result = useQuery({
    queryKey: ["reports", code, params],
    queryFn: () => api.get<ReportResult>(`/reports/${code}`, { params }),
    enabled: !!meta && params !== null,
  });
  // ТЗ 1.2 раздел 9.1: saved views — mine and the shared ones; they keep parameters, never rights
  const views = useQuery({
    queryKey: ["reports", code, "views"],
    queryFn: () => api.get<ReportView[]>(`/reports/${code}/views`),
    enabled: !!meta,
  });
  const saveView = useMutation({
    mutationFn: (body: { name: string; is_shared: boolean; params: Record<string, unknown> }) => api.post<ReportView>(`/reports/${code}/views`, body),
    onSuccess: (view) => {
      queryClient.invalidateQueries({ queryKey: ["reports", code, "views"] });
      setViewId(view.id);
      setSaving(false);
      saveForm.resetFields();
      message.success(`Вид «${view.name}» сохранён`);
    },
    onError: (error) => message.error(errorMessage(error)),
  });
  const deleteView = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/reports/views/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports", code, "views"] });
      setViewId(undefined);
      message.success("Вид удалён");
    },
    onError: (error) => message.error(errorMessage(error)),
  });

  const initial = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const p of meta?.params ?? []) {
      if (p.default !== null && p.default !== undefined) values[p.name] = p.default;
      if (p.type === "date" && p.name === "date_from") values[p.name] = dayjs().subtract(7, "day");
      if (p.type === "date" && p.name === "date_to") values[p.name] = dayjs();
    }
    return values;
  }, [meta]);

  const periodRule = ({ getFieldValue }: { getFieldValue: (name: string) => unknown }) => ({
    // D09: a reversed period is an input error, not «no data»
    validator: () => {
      const from = getFieldValue("date_from");
      const to = getFieldValue("date_to");
      if (dayjs.isDayjs(from) && dayjs.isDayjs(to) && from.isAfter(to, "day")) {
        return Promise.reject(new Error("Начало периода позже окончания: поменяйте даты местами"));
      }
      return Promise.resolve();
    },
  });

  const submit = (values: Record<string, unknown>) => {
    setParams(cleanValues(values, meta?.params ?? []));
  };

  const applyView = (id: string | undefined) => {
    setViewId(id);
    const view = views.data?.find((v) => v.id === id);
    if (!view || !meta) return;
    const values = { ...initial, ...formValues(view.params, meta.params) };
    form.setFieldsValue(values);
    submit(values);
  };

  if (!meta) {
    if (metaQuery.isLoading || probe.isLoading) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;
    if (probe.error) return <LoadProblem error={probe.error} />;
    return <LoadProblem error={new ApiError({ title: "Не найдено", status: 404, detail: "Такого отчёта нет" })} />;
  }
  // configurable columns (age ranges) come with the result; the registry's are the fallback
  const shownColumns = result.data?.columns ?? meta.columns;
  const columns = tableColumns(shownColumns);
  const exportHref = `${API_BASE}/reports/${code}.xlsx${qs({ params })}`;
  const selectedView = views.data?.find((v) => v.id === viewId);
  const pdf = reportPdfParams(params, boundWarehouses ?? [], meta.params.some((p) => p.name === "warehouse_id"));

  return (
    <div>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }} wrap>
        <div>
          <Link to="/reports">Отчёты</Link>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {meta.name}
          </Typography.Title>
          <Typography.Text type="secondary">{meta.description}</Typography.Text>
        </div>
        <Space wrap>
        <Button
          icon={<FilePdfOutlined />}
          disabled={pdf.params === null || result.isError}
          onClick={() => setViewing(true)}
          data-testid="report-pdf"
        >
          Просмотр и печать
        </Button>
        <Button
          icon={<DownloadOutlined />}
          disabled={params === null || result.isError}
          onClick={async () => {
            const response = await fetch(exportHref, { headers: downloadHeaders() });
            if (!response.ok) {
              const problem = await response.json().catch(() => null);
              message.error(problem?.detail ?? "Выгрузка не сформирована");
              return;
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${code}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Выгрузить в Excel
        </Button>
        </Space>
      </Space>
      {pdf.params !== null && (
        <DocumentViewer open={viewing} onClose={() => setViewing(false)} kind={`report.${code}`} documentId={code} params={pdf.params} title={meta.name} />
      )}
      {pdf.hint && (
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }} data-testid="report-pdf-hint">
          {pdf.hint}
        </Typography.Text>
      )}
      {params === null && (
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
          «Просмотр и печать» и выгрузка — после «Сформировать» с выбранными параметрами.
        </Typography.Text>
      )}
      <Space style={{ marginBottom: 12 }} wrap data-testid="report-views">
        <Select
          allowClear
          placeholder="Сохранённые виды"
          style={{ minWidth: 260 }}
          value={viewId}
          loading={views.isFetching}
          onChange={(id) => applyView(id)}
          options={(views.data ?? []).map((v) => ({ value: v.id, label: v.is_shared ? `${v.name} (общий, ${v.owner})` : v.name }))}
          data-testid="report-view-select"
        />
        <Button icon={<SaveOutlined />} onClick={() => setSaving(true)} data-testid="report-view-save">
          Сохранить вид
        </Button>
        {selectedView?.can_delete && (
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() =>
              modal.confirm({
                title: `Удалить вид «${selectedView.name}»?`,
                content: selectedView.is_shared ? "Вид общий: коллеги тоже перестанут его видеть." : undefined,
                okText: "Удалить",
                okButtonProps: { danger: true },
                cancelText: "Отмена",
                onOk: () => deleteView.mutateAsync(selectedView.id),
              })
            }
            data-testid="report-view-delete"
          >
            Удалить вид
          </Button>
        )}
      </Space>
      <Modal
        title="Сохранить вид отчёта"
        open={saving}
        onCancel={() => setSaving(false)}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={saveView.isPending}
        onOk={async () => {
          const { name, is_shared } = await saveForm.validateFields();
          saveView.mutate({ name, is_shared: !!is_shared, params: cleanValues(form.getFieldsValue(), meta.params) });
        }}
      >
        <Typography.Paragraph type="secondary">
          Сохраняются только параметры и разрезы. Права на отчёт, выгрузку и склады проверяются заново при каждом запуске — общий вид не расширяет доступ коллег.
        </Typography.Paragraph>
        <Form form={saveForm} layout="vertical" initialValues={{ is_shared: false }}>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: "Назовите вид" }]}>
            <Input maxLength={200} data-testid="report-view-name" />
          </Form.Item>
          <Form.Item name="is_shared" label="Общий вид (видят коллеги с правом на отчёт)" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
      <Form
        form={form}
        layout="inline"
        initialValues={initial}
        onFinish={submit}
        onValuesChange={() => setParams(null)} // stale rows never sit next to changed filters
        style={{ marginBottom: 16, rowGap: 8 }}
      >
        {meta.params.map((p) => (
          <Form.Item
            key={p.name}
            name={p.name}
            label={p.label}
            tooltip={p.description ?? undefined}
            dependencies={p.name === "date_from" ? ["date_to"] : p.name === "date_to" ? ["date_from"] : undefined}
            rules={[...(p.required ? [{ required: true, message: "Обязательно" }] : []), ...(p.type === "date" ? [periodRule] : [])]}
            style={{ minWidth: 220 }}
          >
            <ParamInput param={p} />
          </Form.Item>
        ))}
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={result.isFetching}>
            Сформировать
          </Button>
        </Form.Item>
      </Form>
      {result.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Отчёт не сформирован" description={errorMessage(result.error)} />}
      {result.data?.meta && Object.keys(result.data.meta).length > 0 && <ResultMeta meta={result.data.meta} />}
      <Table
        rowKey={(_, index) => String(index)}
        size="small"
        loading={result.isFetching}
        dataSource={result.data?.rows ?? []}
        columns={columns}
        pagination={{ pageSize: 100, showTotal: (t) => `строк: ${t}` }}
        scroll={{ x: true }}
        expandable={
          meta.drill && params
            ? {
                rowExpandable: (row) => !!row._key,
                expandedRowRender: (row) => <DrillRows code={code} params={params} row={row} columns={meta.drill_columns ?? []} />,
              }
            : undefined
        }
        summary={() =>
          result.data && meta.summary.length ? (
            // CW31-B02: pieces and kilograms are never added up — several units give one «Итого» row per unit
            <>
              {footerRows(meta, result.data).map((row) => (
                <Table.Summary.Row key={row.key} data-testid={`report-${row.key}`}>
                  {shownColumns.map((c, index) => (
                    <Table.Summary.Cell key={c.name} index={index} align={c.type === "number" ? "right" : undefined}>
                      {index === 0 ? row.label : c.name in row.values ? cell(c, row.values[c.name]) : ""}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              ))}
            </>
          ) : null
        }
      />
    </div>
  );
}
